import React, { useCallback, useMemo, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CheckIcon, ScaleIcon, XIcon } from 'lucide-react'

import { Alert, Button, Input, Modal } from '@repo/ui'
import { ConfirmSettingModal, SelectField } from 'components'
import { EmptyState, StatusChip } from 'components/display'
import { Table } from 'components/display/Table/Table'
import { QueryErrorState } from 'components/feedback'
import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown'
import { MARKET_STAT_STATUSES } from 'constants/statuses'
import {
  useApprovedClaimsQuery,
  useApproveClaim,
  useBulkApproveClaims,
  useBulkRejectClaims,
  useProposedClaimsQuery,
  useRejectedClaimsQuery,
} from 'hooks/api'
import { getErrorMessage } from 'utils'
import type { IAskClaim } from 'services/askClaimsApi'

import { MarketClaimDetailModal } from './MarketClaimDetailModal'

type AskClaimStatusFilter = 'pending' | 'approved' | 'rejected'

const pluralize = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** A stat's status is DERIVED from its two timestamps, never stored directly — the
 * two are kept mutually exclusive server-side (`AskClaimsService.reject` clears
 * `approved_at`), so this is a safe, cheap computation rather than a third source
 * of truth. */
const statOf = (stat: IAskClaim): AskClaimStatusFilter =>
  stat.approvedAt ? 'approved' : stat.rejectedAt ? 'rejected' : 'pending'

/**
 * The Stats tab of `/internal/market` — the approval queue, rebuilt as ONE table
 * with bulk actions (VRG-445 review-table rebuild, superseding the card-based
 * design this file used to carry).
 *
 * ## Why this replaces the card design, and what survives from it
 *
 * The old docblock here argued cards exist to PREVENT skimming, because approving
 * a stat is a consequential judgement — a table row truncates the quote, hides the
 * context, and buries the two provenance hops in columns nobody widens. That
 * argument holds only as long as the table DOES those three things. This one
 * doesn't:
 *   - the quote column is WIDE and allows 2 lines (no `noWrap` truncation) —
 *     reading it in the row IS reading the claim, so the objection dissolves;
 *   - the surrounding context + both provenance hops are one click away in
 *     `MarketClaimDetailModal`, not buried in a column nobody widens;
 *   - and cards themselves had a real cost the old docblock didn't count: no
 *     scroll, no search, no bulk action, and they collapsed at volume — a queue
 *     of 30 proposals was 30 stacked cards with nothing to skim it BY.
 *
 * What survives unchanged from the card design: the "Not stated" italic render
 * for a null original source (never hidden — see `MarketClaimDetailModal`'s own
 * comment), the reason-required reject flow (AC6), and the strict
 * pending-only-approvable / not-already-rejected-is-rejectable row rules the
 * service enforces (`AskClaimsService.assertApprovable`).
 *
 * ## The status filter is what makes "Approved" and "Rejected" tables too
 *
 * The old page ran the approval queue (cards) and a small approved table SIDE BY
 * SIDE, on the argument that hiding either makes the other invisible. This
 * version keeps them all reachable — just one filter away, defaulted to Pending
 * (the state that actually needs a human) — rather than permanently spending
 * vertical space on two simultaneous tables. `Table`'s own `loading` skeleton
 * (§19) covers the filter switch, so changing it never shows a blank flash.
 */
export const AskClaimsTab = () => {
  const theme = useTheme()

  const { data: proposed = [], isLoading: proposedLoading, isError: proposedError, error: proposedErr, refetch: refetchProposed } =
    useProposedClaimsQuery()
  const { data: approved = [], isLoading: approvedLoading, isError: approvedError, error: approvedErr, refetch: refetchApproved } =
    useApprovedClaimsQuery()
  const { data: rejected = [], isLoading: rejectedLoading, isError: rejectedError, error: rejectedErr, refetch: refetchRejected } =
    useRejectedClaimsQuery()

  const [statusFilter, setStatusFilter] = useState<AskClaimStatusFilter>('pending')
  const [rowSelectionModel, setRowSelectionModel] = useState<(string | number)[]>([])

  const rows = statusFilter === 'pending' ? proposed : statusFilter === 'approved' ? approved : rejected
  const isLoading = statusFilter === 'pending' ? proposedLoading : statusFilter === 'approved' ? approvedLoading : rejectedLoading
  const isError = statusFilter === 'pending' ? proposedError : statusFilter === 'approved' ? approvedError : rejectedError
  const error = statusFilter === 'pending' ? proposedErr : statusFilter === 'approved' ? approvedErr : rejectedErr
  const refetch = statusFilter === 'pending' ? refetchProposed : statusFilter === 'approved' ? refetchApproved : refetchRejected

  const approve = useApproveClaim()
  const bulkApprove = useBulkApproveClaims()
  const bulkReject = useBulkRejectClaims()

  const [actionError, setActionError] = useState<string | null>(null)
  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    try {
      await fn()
    } catch (e) {
      setActionError(getErrorMessage(e))
    }
  }

  const clearSelection = () => setRowSelectionModel([])

  // ── Detail drawer ──────────────────────────────────────────────────────────
  const [detailStat, setDetailStat] = useState<IAskClaim | null>(null)
  const openDetail = (stat: IAskClaim) => setDetailStat(stat)
  const closeDetail = () => setDetailStat(null)

  /*
    Prev/Next walk the CURRENTLY FILTERED list, not the whole table, so the drawer moves
    through exactly the set the reviewer is looking at. Switching the status filter while
    the drawer is open therefore changes what "next" means, which is the correct reading:
    the filter IS the queue.

    `undefined` at either end rather than wrapping — the drawer's own footer disables a
    button with no handler, so the ends of the list are simply the ends, and neither
    button ever moves you somewhere you did not ask to go.
  */
  const detailIndex = detailStat ? rows.findIndex(r => r.id === detailStat.id) : -1
  const goToOffset = (offset: number) => {
    const next = rows[detailIndex + offset]
    if (next) setDetailStat(next)
  }

  // ── Reject — ONE modal, shared by the row kebab (a single id) and the bulk bar
  // (many ids). Both go through `useBulkRejectClaims`: a batch of one is the
  // same request shape as a batch of many, so there is no reason to keep two
  // mutations doing the same reason-required, transactional write.
  const [rejectTarget, setRejectTarget] = useState<IAskClaim[] | null>(null)
  const [reason, setReason] = useState('')
  const [rejectError, setRejectError] = useState<string | null>(null)

  const openReject = (statsToReject: IAskClaim[]) => {
    setRejectTarget(statsToReject)
    setReason('')
    setRejectError(null)
  }
  const closeReject = () => {
    setRejectTarget(null)
    setReason('')
    setRejectError(null)
  }
  const submitReject = async () => {
    if (!rejectTarget || !reason.trim()) return
    setRejectError(null)
    try {
      await bulkReject.mutateAsync({ ids: rejectTarget.map(s => s.id), reason: reason.trim() })
      closeReject()
      clearSelection()
    } catch (e) {
      setRejectError(getErrorMessage(e) || 'Could not reject.')
    }
  }

  // ── Bulk approve — CONFIRM-GATED (working-conventions §43): the confirm names
  // the actual downstream consequence rather than asking "are you sure?", because
  // approving is what makes a market claim citable by Ask Vera in front of any
  // brand, which a Save-button-shaped click cannot say for itself.
  const [approveTarget, setApproveTarget] = useState<IAskClaim[] | null>(null)
  const [approveError, setApproveError] = useState<string | null>(null)
  const closeApprove = () => {
    setApproveTarget(null)
    setApproveError(null)
  }
  const submitApprove = async () => {
    if (!approveTarget) return
    setApproveError(null)
    try {
      await bulkApprove.mutateAsync(approveTarget.map(s => s.id))
      closeApprove()
      clearSelection()
    } catch (e) {
      setApproveError(getErrorMessage(e) || 'Could not approve those statistics.')
    }
  }

  const rowActions = (row: IAskClaim): ActionDropdownItem[] => [
    {
      label: 'Approve',
      icon: <CheckIcon strokeWidth={1} />,
      // RECORD STATE -> hidden, not disabled (§46): a stat that is already
      // approved or was rejected has nothing this action can do — approving an
      // already-approved row is a no-op, and `AskClaimsService.approve` refuses
      // a rejected one outright (re-extract instead).
      visible: !row.approvedAt && !row.rejectedAt,
      onClick: () => run(() => approve.mutateAsync(row.id)),
    },
    {
      label: 'Reject',
      icon: <XIcon strokeWidth={1} />,
      destructive: true,
      // Visible on an APPROVED row too — rejecting retracts it (the service
      // clears `approved_at` on reject), so this is the row's way to undo an
      // approval it should not have gotten. Hidden only once already rejected.
      visible: !row.rejectedAt,
      onClick: () => openReject([row]),
    },
  ]

  const getBulkActions = useCallback((selectedRows: IAskClaim[]) => {
    const approvable = selectedRows.filter(r => !r.approvedAt && !r.rejectedAt)
    const rejectable = selectedRows.filter(r => !r.rejectedAt)
    return [
      {
        label: 'Approve',
        icon: <CheckIcon strokeWidth={1} />,
        onClick: () => setApproveTarget(approvable),
        // Record state, same rule as the row-level action: only pending rows can
        // be approved, so the button is offered only when at least one qualifies.
        visible: approvable.length > 0,
      },
      {
        label: 'Reject',
        icon: <XIcon strokeWidth={1} />,
        destructive: true,
        onClick: () => openReject(rejectable),
        visible: rejectable.length > 0,
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns = useMemo(
    () => [
      {
        field: 'verbatimQuote',
        headerName: 'Claim',
        type: 'string',
        /*
          ~2x the search box (248px), authored rather than flexed.

          It was `flex: 4`, which took most of the grid and read as one enormous column
          beside a huddle of small ones. It no longer needs that width: a claim is now a
          complete self-contained sentence (extraction prompt rule 2), so it wraps to two
          lines at any sane width instead of needing one very long line. The column still
          carries the "a table is acceptable here" argument from the file docblock —
          that argument is about NOT TRUNCATING, which two wrapped lines satisfy.

          Authored for the same reason the Content tab's Title column is: `Table`'s
          proportional-width normalisation bails out if ANY column declares flex, so one
          flex column opts the whole table out of the protection it needs.
        */
        width: 500,
        sortable: false,
        renderCell: ({ row }: { row: IAskClaim }) => (
          // The shared `Table` has no `onRowClick` pass-through (only per-cell
          // `renderCell`), so the quote cell itself is the click target — the
          // same convention the Content tab uses for its Title cell.
          <Box
            role="button"
            tabIndex={0}
            onClick={() => openDetail(row)}
            onKeyDown={e => {
              if (e.key === 'Enter') openDetail(row)
            }}
            sx={{ cursor: 'pointer', width: '100%', py: 1 }}
          >
            <Typography
              variant="body3"
              title={row.verbatimQuote}
              sx={{
                lineHeight: 1.4,
                // Two lines, then an ellipsis — allowed room to actually be read
                // (§49's spirit applied to a value cell rather than a header:
                // the whole point of this column is that the claim is legible
                // without opening anything), with the full text one click away
                // in the drawer for the rare quote that runs longer.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'normal',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {row.verbatimQuote}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'originalSource',
        headerName: 'Original source',
        type: 'string',
        flex: 1,
        minWidth: 170,
        sortable: true,
        renderCell: ({ row }: { row: IAskClaim }) =>
          row.originalSource ? (
            <Typography variant="body3" color="text.secondary" noWrap>
              {row.originalSource}
            </Typography>
          ) : (
            // "Not stated" is rendered, never hidden — a missing original source
            // is the single most important thing a reviewer can know (it means
            // the figure is second-hand with no traceable origin).
            <Typography variant="body3" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Not stated
            </Typography>
          ),
      },
      {
        field: 'asReportedBy',
        headerName: 'As reported by',
        type: 'string',
        flex: 1,
        minWidth: 160,
        sortable: true,
        renderCell: ({ row }: { row: IAskClaim }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.asReportedBy ?? '—'}
          </Typography>
        ),
      },
      {
        // Sized for the HEADER, not the values (§49).
        field: 'category',
        headerName: 'Category',
        type: 'string',
        width: 140,
        sortable: true,
        renderCell: ({ row }: { row: IAskClaim }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.category ?? '—'}
          </Typography>
        ),
      },
      {
        field: 'period',
        headerName: 'Period',
        type: 'string',
        width: 120,
        sortable: true,
        renderCell: ({ row }: { row: IAskClaim }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.period ?? '—'}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 130,
        sortable: true,
        /*
          NOT filterable, deliberately. Status is already the table's top-level control
          beside the search box, and the Filters drawer derives its options from the
          columns — so leaving this filterable put the same concept in two places that
          could disagree with each other. The drawer keeps every OTHER column; it just
          does not get a second opinion about status.
        */
        filterable: false,
        // A computed field, not a stored column — see `statOf`'s own doc.
        valueGetter: (_value: unknown, row: IAskClaim) => statOf(row),
        renderCell: ({ row }: { row: IAskClaim }) => (
          <StatusChip statuses={MARKET_STAT_STATUSES} status={statOf(row)} size="small" />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 64,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: IAskClaim }) => <ActionDropdown items={rowActions(row)} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )

  const emptyStateFor = (filter: AskClaimStatusFilter) => {
    if (filter === 'pending') {
      return (
        <EmptyState
          icon={<ScaleIcon strokeWidth={1} />}
          title="Nothing waiting for review"
          text="Run extraction on a piece of content and any statistics it proposes queue up here. Nothing reaches Ask Vera until you approve it."
        />
      )
    }
    if (filter === 'approved') {
      return (
        <EmptyState
          icon={<CheckIcon strokeWidth={1} />}
          title="Nothing approved yet"
          text="Approve a proposal from the Pending filter and it becomes citable by Ask Vera."
        />
      )
    }
    return (
      <EmptyState
        icon={<XIcon strokeWidth={1} />}
        title="Nothing rejected"
        text="A rejected proposal — and the reason it was rejected — shows up here."
      />
    )
  }

  return (
    <Box>
      {actionError && (
        <Typography variant="body3" sx={{ color: theme.palette.error.main, lineHeight: 1.5, mb: 3 }}>
          {actionError}
        </Typography>
      )}

      {isError ? (
        <QueryErrorState
          error={error}
          subject="the approval queue"
          permissionLabel="Add items by URL and set their review status"
          onRetry={() => refetch()}
        />
      ) : (
        <Table
          loading={isLoading}
          columns={columns}
          rows={rows}
          getRowId={(row: IAskClaim) => row.id}
          bulkActions={getBulkActions}
          rowSelectionModel={rowSelectionModel as any}
          onRowSelectionModelChange={model => setRowSelectionModel(model as (string | number)[])}
          // This table IS the tab's whole content (no cards above it, no second
          // table below it any more) — a full-page table under §22, so it fills
          // to the bottom the same distance as the page gutters rather than
          // stopping at an arbitrary height.
          fillBottom
          actionLineRightComponents={
            /*
              `minWidth`, NOT a fixed `width: 200` on a wrapping Box. That is what this
              was, and it forced the toolbar's right-hand slot into a COLUMN: the status
              control and the Download button ended up stacked at the same left edge,
              both 200px wide, 52px apart, with Download overlapping the table header.

              Measured before: Status at (1336, 173, w200), Download at (1336, 225, w200)
              while Filter/Sort/Layout sat correctly at y=201. The control needs a floor
              so the "Status: Pending (3)" label does not truncate as the count changes,
              and nothing more than that.
            */
            <Box sx={{ minWidth: 180, flexShrink: 0 }}>
              <SelectField
                value={statusFilter}
                onChange={value => {
                  setStatusFilter(value as AskClaimStatusFilter)
                  clearSelection()
                }}
                /*
                  The trigger shows the selected status as the SAME pill the rows use,
                  with its count beside it — so the control reads as "you are looking at
                  these 20 pending" rather than as a dropdown whose value happens to be a
                  word. `valuePrefix` is dropped with it: "Status: Pending" spent a third
                  of the control restating the column header, and a pill needs no label to
                  say what kind of thing it is.
                */
                renderTriggerValue={() => (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <StatusChip
                      statuses={MARKET_STAT_STATUSES}
                      status={statusFilter}
                      size="small"
                    />
                    <Typography variant="body3" color="text.secondary">
                      {rows.length}
                    </Typography>
                  </Stack>
                )}
                options={[
                  // Counts stay in the OPTIONS — that is where they help you choose. The
                  // trigger shows the count for the one you already picked.
                  { label: `Pending${proposed.length ? ` (${proposed.length})` : ''}`, value: 'pending' },
                  { label: `Approved${approved.length ? ` (${approved.length})` : ''}`, value: 'approved' },
                  { label: `Rejected${rejected.length ? ` (${rejected.length})` : ''}`, value: 'rejected' },
                ]}
              />
            </Box>
          }
          emptyState={emptyStateFor(statusFilter)}
        />
      )}

      <MarketClaimDetailModal
        open={Boolean(detailStat)}
        onClose={closeDetail}
        stat={detailStat}
        isActing={bulkApprove.isPending || bulkReject.isPending}
        onApprove={
          detailStat && statOf(detailStat) === 'pending'
            ? () => setApproveTarget([detailStat])
            : undefined
        }
        onReject={
          detailStat && statOf(detailStat) === 'pending'
            ? () => openReject([detailStat])
            : undefined
        }
        onPrevious={detailIndex > 0 ? () => goToOffset(-1) : undefined}
        onNext={detailIndex >= 0 && detailIndex < rows.length - 1 ? () => goToOffset(1) : undefined}
      />

      <Modal
        open={Boolean(rejectTarget)}
        title={rejectTarget && rejectTarget.length > 1 ? `Reject ${rejectTarget.length} proposals` : 'Reject this proposal'}
        onClose={closeReject}
        description={
          <>
            The reason is kept
            {rejectTarget && rejectTarget.length > 1
              ? ', applied to every one of the selected proposals. '
              : '. '}
            It is the only signal for whether extraction is good enough to widen later, so
            “wrong” is less useful than “the figure is about the US, not global” or “that
            number is the 2019 figure, not 2024”.
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={closeReject}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitReject}
              disabled={!reason.trim()}
              loading={bulkReject.isPending}
            >
              {rejectTarget && rejectTarget.length > 1 ? `Reject ${rejectTarget.length}` : 'Reject'}
            </Button>
          </>
        }
      >
        {rejectError && <Alert severity="error" description={rejectError} sx={{ mt: 1, mb: 3 }} />}
        <Input
          label="Why is this wrong?"
          placeholder="The article attributes this to a 2019 study, not 2024"
          value={reason}
          onChange={(value: string) => {
            setReason(value)
            if (rejectError) setRejectError(null)
          }}
          error={Boolean(rejectError)}
          disabled={bulkReject.isPending}
        />
      </Modal>

      <ConfirmSettingModal
        open={Boolean(approveTarget)}
        onClose={closeApprove}
        onConfirm={submitApprove}
        title={`Approve ${pluralize(approveTarget?.length ?? 0, 'statistic')}?`}
        consequence={`Each becomes citable by Ask Vera in answers to any brand. Once approved, a statistic can be surfaced to any company that asks a related market question.`}
        confirmLabel={`Approve ${pluralize(approveTarget?.length ?? 0, 'statistic')}`}
        loading={bulkApprove.isPending}
      >
        {approveError && <Alert severity="error" description={approveError} />}
      </ConfirmSettingModal>
    </Box>
  )
}
